// SnorkelingPage.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import snorkelingMainImage from '../assets/Snorkeling/snorkeling-marine-life.jpeg';
import coralReefImage from '../assets/Snorkeling/Diving-and-Snorkeling-Sri-Lanka.jpg';
import marineLifeImage from '../assets/Snorkeling/caption.jpg';



const SnorkelingPage = () => {
    const { t } = useTranslation();

    return (
        <div className="bg-black py-10 ">
            {/* ===== MAIN CONTENT SECTION (BLACK BG) ===== */}

            <div className="container mx-auto px-4">
                {/* Page Title */}
                <h1
                    className="text-center text-4xl md:text-5xl lg:text-6xl font-bold font-konkhmer mb-16"
                    style={{
                        background: "linear-gradient(to right, #009CE6, #96BA33)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                    }}
                >
                    {t('snorkelingPage.heroTitle')}
                </h1>

                {/* Main Content: Image + Text */}
                <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
                    {/* Left: Main Image */}
                    <div className="w-full lg:w-1/3">
                        <motion.div
                            whileHover={{ scale: 1.03 }}
                            className="group relative overflow-hidden rounded-2xl shadow-2xl"
                        >
                            <img
                                src={snorkelingMainImage}
                                alt={t('snorkelingPage.images.main')}
                                className="w-full h-auto max-h-[500px] object-cover transition-transform duration-500 group-hover:scale-110"
                                loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                                <p className="text-white text-lg font-semibold">{t('snorkelingPage.images.main')}</p>
                            </div>
                        </motion.div>
                    </div>

                    {/* Right: Text Content */}
                    <div className="w-full lg:w-2/3 space-y-8 text-white font-inknut">
                        <p className="text-base md:text-lg lg:text-xl leading-relaxed">
                            {t('snorkelingPage.intro.paragraph1')}
                        </p>
                        <p className="text-base md:text-lg lg:text-xl leading-relaxed">
                            {t('snorkelingPage.intro.paragraph2')}
                        </p>
                    </div>
                </div>

                {/* Why Choose Section */}
                <div className="mt-16">
                    <h2
                        className="text-center text-4xl md:text-5xl lg:text-6xl font-bold font-konkhmer mb-10"
                        style={{
                            background: "linear-gradient(to right, #009CE6, #96BA33)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                        }}
                    >
                        {t('snorkelingPage.highlights.title')}
                    </h2>

                    <div className="bg-white rounded-2xl p-8 md:p-12 shadow-2xl">
                        <p className="text-gray-800 text-base md:text-lg lg:text-xl leading-relaxed font-inknut mb-6">
                            {t('snorkelingPage.highlights.intro')}
                        </p>
                        <ul className="space-y-4 text-gray-800 text-base md:text-lg lg:text-xl font-inknut">
                            {t('snorkelingPage.highlights.list', { returnObjects: true }).map((item, idx) => (
                                <li key={idx} className="flex items-start">
                                    <span className="text-[#009CE6] mr-3">•</span>
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Additional Images */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16">
                    <motion.div
                        whileHover={{ scale: 1.03 }}
                        className="group relative overflow-hidden rounded-2xl shadow-2xl"
                    >
                        <img
                            src={coralReefImage}
                            alt={t('snorkelingPage.images.coral')}
                            className="w-full h-80 md:h-96 object-cover transition-transform duration-500 group-hover:scale-110"
                            loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                            <p className="text-white text-lg font-semibold">{t('snorkelingPage.images.coral')}</p>
                        </div>
                    </motion.div>

                    <motion.div
                        whileHover={{ scale: 1.03 }}
                        className="group relative overflow-hidden rounded-2xl shadow-2xl"
                    >
                        <img
                            src={marineLifeImage}
                            alt={t('snorkelingPage.images.marine')}
                            className="w-full h-80 md:h-96 object-cover transition-transform duration-500 group-hover:scale-110"
                            loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                            <p className="text-white text-lg font-semibold">{t('snorkelingPage.images.marine')}</p>
                        </div>
                    </motion.div>
                </div>

                {/* Tips Section */}
                <div className="mt-16">
                    <h2
                        className="text-center text-4xl md:text-5xl lg:text-6xl font-bold font-konkhmer mb-10"
                        style={{
                            background: "linear-gradient(to right, #009CE6, #96BA33)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                        }}
                    >
                        {t('snorkelingPage.tips.title')}
                    </h2>

                    <div className="bg-white rounded-2xl p-8 md:p-12 shadow-2xl">
                        <ul className="space-y-4 text-gray-800 text-base md:text-lg lg:text-xl font-inknut">
                            {t('snorkelingPage.tips.list', { returnObjects: true }).map((tip, idx) => (
                                <li key={idx} className="flex items-start">
                                    <span className="text-[#009CE6] mr-3">•</span>
                                    <span>{tip}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default SnorkelingPage;